/**
 * Live Upload (batch) — generic bounded-concurrency pool.
 *
 * Runs at most `concurrency` `worker` invocations at once, starting the next as
 * soon as one settles. Results are returned in INPUT order (not completion
 * order). A worker that rejects yields a `rejected` settled result; the pool
 * keeps going (failure isolation), so callers get one entry per input.
 */
export async function runPool<T, R>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results = Array.from<PromiseSettledResult<R>>({ length: items.length });
  let next = 0;
  const limit = Math.max(1, Math.min(concurrency, items.length || 1));

  async function drain(): Promise<void> {
    while (next < items.length) {
      const i = next++;
      try {
        results[i] = { status: "fulfilled", value: await worker(items[i], i) };
      } catch (reason) {
        results[i] = { status: "rejected", reason };
      }
    }
  }

  await Promise.all(Array.from({ length: limit }, () => drain()));
  return results;
}
